import { memo, type ReactElement, useMemo } from 'react';
import { plural, select } from '@frameio/i18n';
import {
  Button2 as Button,
  Menu,
  MenuCheckbox,
  MenuGroup,
  MenuPrimaryAction,
} from '@frameio/vapor';
import {
  IconBlank,
  IconChevronDown,
  IconFolderClosed,
  IconProject,
} from '@frameio/vapor-icons';
import type { FilterKeys, QuickSearchFilters } from '../../types';
import TEXT from './generated/TEXT/QuickSearchFilter';
import styles from './QuickSearchFilter.module.css';

TEXT.DEFINE(
  {
    /** Clear filters button label and aria-label */
    clearAll: 'Clear All',
    /** Text label for the filter menu trigger */
    filterTrigger: 'Filter',
    /** Accessible label for the filter menu trigger when no filters are active */
    filterTriggerAriaLabel: 'Filter results',
    /** Accessible label for the filter menu trigger when filters are active */
    filterTriggerAriaLabelWithCount: (count: number): string =>
      `Filter results, ${count} active ${plural(count, 'filters', 'filter')}`,
    /** Menu group label describing what the checkboxes filter */
    menuLabel: 'Filter results by',
    /** Filter option label based on filter key */
    filterOptionLabel: (key: FilterKeys): string =>
      select(key, {
        assets: 'Asset',
        folders: 'Folder',
        projects: 'Project',
        other: '',
      }),
  },
  { select }
);

type QuickSearchFilterProps = {
  filters: QuickSearchFilters;
  onToggleFilter: (filter: FilterKeys, value: boolean) => void;
  onClearAllFilters: () => void;
  isScopedToProject?: boolean;
};

const FILTER_KEYS: FilterKeys[] = ['assets', 'folders', 'projects'];

const FILTER_ICONS: Record<FilterKeys, ReactElement> = {
  assets: <IconBlank />,
  folders: <IconFolderClosed />,
  projects: <IconProject />,
};

const QuickSearchFilter = memo(
  ({
    filters,
    onToggleFilter,
    onClearAllFilters,
    isScopedToProject = false,
  }: QuickSearchFilterProps) => {
    const applicableFilterKeys = useMemo(
      () =>
        isScopedToProject
          ? FILTER_KEYS.filter((key) => key !== 'projects')
          : FILTER_KEYS,
      [isScopedToProject]
    );
    const activeFilterKeys = useMemo(
      () => new Set(applicableFilterKeys.filter((key) => filters[key])),
      [applicableFilterKeys, filters]
    );

    const activeFilterCount = activeFilterKeys.size;
    const hasActiveFilter = activeFilterCount > 0;

    const filterTriggerAriaLabel = hasActiveFilter
      ? TEXT.filterTriggerAriaLabelWithCount(activeFilterCount)
      : TEXT.filterTriggerAriaLabel;

    return (
      <Menu
        content={
          <MenuGroup className={styles.filterMenuGroup} label={TEXT.menuLabel}>
            {applicableFilterKeys.map((key) => {
              const isActive = Boolean(filters[key]);

              return (
                <MenuCheckbox
                  key={key}
                  checked={isActive}
                  iconBefore={FILTER_ICONS[key]}
                  name={key}
                  onSelect={() => onToggleFilter(key, !isActive)}
                  shouldCloseMenuOnSelect={false}
                >
                  {TEXT.filterOptionLabel(key)}
                </MenuCheckbox>
              );
            })}
            <MenuPrimaryAction
              disabled={!hasActiveFilter}
              onClick={onClearAllFilters}
              shouldCloseMenuOnSelect={false}
            >
              {TEXT.clearAll}
            </MenuPrimaryAction>
          </MenuGroup>
        }
        minWidth="244px"
        placement="bottom-start"
      >
        <Button
          aria-label={filterTriggerAriaLabel}
          className={styles.filterTrigger}
          iconAfter={<IconChevronDown />}
          shape="rectangular"
          size="small"
          variant="tertiary"
        >
          {TEXT.filterTrigger}
          {hasActiveFilter ? ` (${activeFilterCount})` : null}
        </Button>
      </Menu>
    );
  }
);

QuickSearchFilter.displayName = 'QuickSearchFilter';

export { QuickSearchFilter };
